// collector/dayView.js
//
// Pure view helpers for the collector's screens (collector-app P1-C part B):
// what a shop row says, which chip it wears, which shop is «دلوقتي عندك», and
// the counting words. Everything here READS the day document the backend
// built (brief 2026-09-30-collector-day-read); none of it derives a state the
// backend owns. A shop's `state` is the backend's — this file only maps it to
// a chip, and never decides «خلّصت» on its own.
//
// Returned strings are i18n KEYS + params (the `collector.*` namespace, staff
// copy taken verbatim from the accepted mockups), so the pages stay the only
// place that calls `t()` and these stay testable without a locale.

/** The shop states the day read sends (L-C), done first. */
export const SHOP_STATES = Object.freeze({
  DONE_MATCHED: 'done_matched',
  DONE_DIFF: 'done_diff',
  DONE_NO_PAPER: 'done_no_paper',
  CLOSED_BY_COLLECTOR: 'closed_by_collector',
  IN_PROGRESS: 'in_progress',
  NOT_STARTED: 'not_started',
});

const DONE_STATES = new Set([
  SHOP_STATES.DONE_MATCHED,
  SHOP_STATES.DONE_DIFF,
  SHOP_STATES.DONE_NO_PAPER,
]);

/** A shop the collector has finished (any `done_*` state from the backend). */
export function isDoneState(state) {
  return DONE_STATES.has(state);
}

/**
 * A shop whose invoice is filed on the phone and not sent yet (dayModel.js
 * `withQueuedInvoices`), while the server still reads it open. He is finished
 * there, so it wears its own chip and «دلوقتي عندك» moves on — but it is NOT
 * done: a done or closed state from the server always wins over the flag.
 */
export function invoiceWaits(shop) {
  return (
    shop?.invoiceQueued != null &&
    (shop.state === SHOP_STATES.IN_PROGRESS || shop.state === SHOP_STATES.NOT_STARTED)
  );
}

/**
 * The plural bucket Egyptian counting words need: 1 «صنف», 2 «صنفين»,
 * 3–10 «أصناف», 11+ «صنف». The locale holds one string per bucket.
 * @param {number} n
 * @returns {'one'|'two'|'few'|'many'}
 */
export function countBucket(n) {
  const value = Math.abs(Number(n) || 0);
  if (value === 1) return 'one';
  if (value === 2) return 'two';
  if (value >= 3 && value <= 10) return 'few';
  return 'many';
}

/** `{ key, params }` for a counted noun, e.g. countPhrase('items', 6). */
export function countPhrase(noun, n) {
  return { key: `collector.count.${noun}.${countBucket(n)}`, params: { n } };
}

/**
 * The counted word that stands NEXT TO a numeral («3 قطع», «12 قطعة») — the
 * figures row and the quantity square print the number large and the word
 * small. It is the phrase's own word with the number left out; two takes the
 * plural, because «قطعتين» already holds its number and cannot follow a «2».
 * @param {string} noun - a `collector.count.*` family, e.g. 'pieces'
 * @param {number} n
 * @param {Function} t - vue-i18n `t`
 */
export function countWord(noun, n, t) {
  const bucket = countBucket(n);
  return t(`collector.count.${noun}.${bucket === 'two' ? 'few' : bucket}`, { n: '' }).trim();
}

/**
 * The shop to go to next: the first `in_progress`, else the first
 * `not_started`, in the leader's route order (spec Section 2.1). A shop whose
 * invoice waits on the phone is skipped: he filed it and moved on. Null when
 * every shop is done, closed or filed.
 */
export function currentShop(day) {
  const shops = (Array.isArray(day?.shops) ? day.shops : []).filter((shop) => !invoiceWaits(shop));
  return (
    shops.find((shop) => shop?.state === SHOP_STATES.IN_PROGRESS) ??
    shops.find((shop) => shop?.state === SHOP_STATES.NOT_STARTED) ??
    null
  );
}

/**
 * The state chip on a shop row (spec Section 2.1; design Section 4.1 for the
 * no-paper wording the mockup does not draw). `not_started` wears none; a
 * shop whose invoice waits on the phone says so, whichever of the two open
 * states the server has it in.
 * @returns {{ tone: 'green'|'amber'|'grey'|'lilac', key: string, params?: object, diffCents?: number } | null}
 */
export function shopStateChip(shop) {
  if (invoiceWaits(shop)) return { tone: 'lilac', key: 'collector.shopChip.invoiceQueued' };
  switch (shop?.state) {
    case SHOP_STATES.DONE_MATCHED:
      return { tone: 'green', key: 'collector.shopChip.done' };
    case SHOP_STATES.DONE_DIFF:
      return {
        tone: 'amber',
        key: 'collector.shopChip.diff',
        diffCents: Number(shop?.invoice?.differenceCents) || 0,
      };
    case SHOP_STATES.DONE_NO_PAPER:
      return { tone: 'green', key: 'collector.shopChip.noPaper' };
    case SHOP_STATES.CLOSED_BY_COLLECTOR:
      return { tone: 'grey', key: 'collector.shopChip.closed' };
    case SHOP_STATES.IN_PROGRESS:
      return { tone: 'lilac', key: 'collector.shopChip.inProgress' };
    default:
      return null;
  }
}

/** «تسلّم مرتجعين» when the shop has returns to hand back, else null. */
export function returnsChip(shop) {
  const count = Array.isArray(shop?.returnsToHandBack) ? shop.returnsToHandBack.length : 0;
  if (count === 0) return null;
  return { tone: 'amber', ...countPhrase('handBackReturns', count) };
}

/** What the shop row's leading circle shows. */
export function shopMarker(shop) {
  if (isDoneState(shop?.state)) return 'done';
  if (shop?.state === SHOP_STATES.CLOSED_BY_COLLECTOR) return 'closed';
  if (invoiceWaits(shop)) return 'number';
  if (shop?.state === SHOP_STATES.IN_PROGRESS) return 'current';
  return 'number';
}

/**
 * A line that is released — cancelled or skipped by the leader. It still
 * shows on its shop (as «لاغي»), but the backend counts it nowhere.
 */
export function isReleased(line) {
  return line?.outcome === 'cancelled' || line?.outcome === 'skipped';
}

const LEAD_MARKS = Object.freeze({
  sent: 'collector.shopRow.leadSent',
  refused: 'collector.shopRow.leadRefused',
  pending: 'collector.shopRow.leadPending',
});

/**
 * The pieces of a shop row's sub-line, in the mockup's order, as
 * `{ key, params }` (joined with « · » by the page):
 * «5 أصناف · 3 اتجابوا · 2 مش موجود», «3 أصناف · من غير علامة»,
 * «4 أصناف · 4 اتجابوا · فاتورة 1,860 مظبوطة», «6 أصناف · باقي صنفين · عربون مدفوع على صنف»
 * (a shop in progress or not started drops the collected and not-found counts).
 *
 * Last, «عرض جاهز 08:12» when the phone holds a usable copy of the shop's
 * supplier preview, then where its «المحل مهتم» stands — «المحل مهتم ✓»,
 * «الاهتمام ماوصلش — قول للمسؤول», «المحل مهتم · مستني النت»
 * (supplier-preview-offline L-M).
 *
 * @param {object} shop - a `shops[]` entry of the day read
 * @param {{ remaining?: boolean, previewReadyAt?: string|null, leadStatus?: 'pending'|'sent'|'refused'|null }} [options] -
 *   `remaining: false` leaves «باقي …» out (the «دلوقتي عندك» card words it its
 *   own way); `previewReadyAt` = when the shop's usable copy was recorded;
 *   `leadStatus` = the offline lead's (collector/previewLeads.js `leadStatuses`)
 */
export function shopSublineParts(shop, { remaining = true, previewReadyAt = null, leadStatus = null } = {}) {
  const counters = shop?.counters ?? {};
  const total = Number(counters.total) || 0;
  const collected = Number(counters.collected) || 0;
  const oos = Number(counters.oos) || 0;
  const pending = Number(counters.pending) || 0;
  const parts = [countPhrase('items', total)];

  // A shop still being worked keeps the sub-line short (it stopped wrapping): the
  // counts live in the progress bar and inside the shop, and the row's amount says
  // what is left. Done and closed rows are history, so they keep them.
  const open = shop?.state === SHOP_STATES.IN_PROGRESS || shop?.state === SHOP_STATES.NOT_STARTED;
  if (!open && collected > 0) parts.push({ key: 'collector.shopRow.collected', params: { n: collected } });
  if (!open && oos > 0) parts.push({ key: 'collector.shopRow.oos', params: { n: oos } });

  if (shop?.state === SHOP_STATES.CLOSED_BY_COLLECTOR) {
    parts.push({ key: 'collector.shopRow.unmarked' });
  } else if (shop?.state === SHOP_STATES.DONE_MATCHED && shop?.invoice?.amountCents != null) {
    parts.push({ key: 'collector.shopRow.invoiceMatched', amountCents: shop.invoice.amountCents });
  } else if (remaining && pending > 0 && shop?.state === SHOP_STATES.IN_PROGRESS) {
    parts.push({ key: 'collector.shopRow.remaining', nested: countPhrase('items', pending) });
  }

  const withDeposit = (shop?.lines ?? []).filter(
    (line) => line?.outcome === 'pending' && Number(line?.depositCents) > 0,
  ).length;
  if (withDeposit > 0 && !isDoneState(shop?.state)) {
    parts.push({ key: 'collector.shopRow.deposit', nested: countPhrase('items', withDeposit) });
  }

  const readyClock = formatClock(previewReadyAt);
  if (readyClock) parts.push({ key: 'collector.shopRow.previewReady', params: { time: readyClock } });
  if (LEAD_MARKS[leadStatus]) parts.push({ key: LEAD_MARKS[leadStatus] });

  return parts;
}

/**
 * One sub-line part → text. `nested` parts carry a counted phrase as their
 * `items` param («باقي صنفين»); amount parts carry cents for `amount`.
 * @param {object} part - from shopSublineParts / countPhrase
 * @param {Function} t - vue-i18n `t`
 * @param {Function} pounds - cents → whole-pound number text
 */
export function phraseText(part, t, pounds) {
  if (!part) return '';
  if (part.nested) return t(part.key, { items: t(part.nested.key, part.nested.params) });
  if (part.amountCents != null) return t(part.key, { amount: pounds(part.amountCents) });
  return t(part.key, part.params ?? {});
}

/** Progress bar widths in percent (spec Section 2.1): collected, not found, the rest. */
export function progressSegments(progress) {
  const lines = Number(progress?.lines) || 0;
  if (lines <= 0) return { collected: 0, oos: 0, rest: 100 };
  const collected = Math.min(100, ((Number(progress?.collected) || 0) / lines) * 100);
  const oos = Math.min(100 - collected, ((Number(progress?.oos) || 0) / lines) * 100);
  return { collected, oos, rest: Math.max(0, 100 - collected - oos) };
}

/**
 * «دفعت في المحلات»: the header's `spentCents` is GROSS, so the figure that
 * adds up with «استلمت الصبح» and «الكاش معاك» is spent − deposits (brief).
 */
export function paidAtShopsCents(cash) {
  return (Number(cash?.spentCents) || 0) - (Number(cash?.depositsCents) || 0);
}

/** «−60»: the sign is U+2212 and stays on the digits (Intl keeps it inside its LRM). */
export function signedPounds(cents) {
  const whole = Number(cents) % 100 === 0;
  return new Intl.NumberFormat('ar-AE', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  })
    .format((Number(cents) || 0) / 100)
    .replace('-', '−');
}

// ── Dates and times — Western digits always (owner-locked UI rule) ────────────

/** «الأحد 28 سبتمبر» from the plan's date-only string ("2026-09-28"). */
export function formatPlanDate(isoDate) {
  if (!isoDate) return null;
  const date = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  const opts = { timeZone: 'UTC' };
  const weekday = new Intl.DateTimeFormat('ar-EG-u-nu-latn', { ...opts, weekday: 'long' }).format(date);
  const dayMonth = new Intl.DateTimeFormat('ar-EG-u-nu-latn', { ...opts, day: 'numeric', month: 'long' }).format(date);
  return `${weekday} ${dayMonth}`;
}

/** «10:42» in Cairo time, from an ISO timestamp. */
export function formatClock(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Africa/Cairo',
  }).format(date);
}
